import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AdminBlogs.css";
import { hasPermission } from "../utils/permissions";

const API = "https://mean3.com/backend/api/blogs.php";

const IMAGE_BASE_URL = "https://mean3.com/";

const initialFormData = {
  title: "",
  handle: "",
  author: "",
  publishDate: "",
  metaTitle: "",
  metaDescription: "",
  image: null,
  content: "",
  status: "published",
};

const AdminBlogs = () => {
  const [showForm, setShowForm] = useState(false);

  const [blogs, setBlogs] = useState([]);

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const [search, setSearch] = useState("");

  const [error, setError] = useState("");

  const [formData, setFormData] = useState(initialFormData);

  const [htmlMode, setHtmlMode] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | QUILL CONFIGURATION
  |--------------------------------------------------------------------------
  */

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],

      ["bold", "italic", "underline", "strike"],

      [{ list: "ordered" }, { list: "bullet" }],

      [{ align: [] }],

      ["blockquote", "code-block"],

      ["link", "image"],

      ["clean"],
    ],
  };

  const quillFormats = [
    "header",

    "bold",
    "italic",
    "underline",
    "strike",

    "list",
    "bullet",

    "align",

    "blockquote",
    "code-block",

    "link",
    "image",
  ];

  /*
  |--------------------------------------------------------------------------
  | FETCH BLOGS
  |--------------------------------------------------------------------------
  */

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API}?t=${Date.now()}`, {
        cache: "no-store",
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to fetch blogs.");
      }

      setBlogs(result.blogs || []);
    } catch (error) {
      console.error("FETCH BLOGS ERROR:", error);

      setError(error.message || "Unable to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOAD BLOGS ON PAGE LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchBlogs();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | NORMAL INPUT CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,

        [name]: files?.[0] || null,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | RICH TEXT CHANGE
  |--------------------------------------------------------------------------
  */

  const handleContentChange = (content) => {
    setFormData((prev) => ({
      ...prev,

      content,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | RESET FORM
  |--------------------------------------------------------------------------
  */

  const resetForm = () => {
    setFormData(initialFormData);

    setEditingId(null);

    const fileInput = document.querySelector('.blog-form input[type="file"]');

    if (fileInput) {
      fileInput.value = "";
    }
  };

  /*
  |--------------------------------------------------------------------------
  | OPEN CREATE FORM
  |--------------------------------------------------------------------------
  */

  const openCreateForm = () => {
    resetForm();

    setShowForm(true);
  };

  /*
  |--------------------------------------------------------------------------
  | CREATE / UPDATE BLOG
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      setError("");

      const data = new FormData();

      /*
      |--------------------------------------------------------------------------
      | ID ONLY FOR UPDATE
      |--------------------------------------------------------------------------
      */

      if (editingId) {
        data.append("id", editingId);
      }

      /*
      |--------------------------------------------------------------------------
      | BLOG DATA
      |--------------------------------------------------------------------------
      */

      data.append("title", formData.title);

      data.append("handle", formData.handle);

      data.append("author", formData.author);

      data.append("publish_date", formData.publishDate);

      data.append("meta_title", formData.metaTitle);

      data.append("meta_description", formData.metaDescription);

      data.append("content", formData.content);

      data.append("status", formData.status);

      /*
      |--------------------------------------------------------------------------
      | IMAGE
      |--------------------------------------------------------------------------
      */

      if (formData.image) {
        data.append("image", formData.image);
      }

      /*
      |--------------------------------------------------------------------------
      | API REQUEST
      |--------------------------------------------------------------------------
      */

      const response = await fetch(API, {
        method: "POST",

        body: data,
      });

      const result = await response.json();

      console.log("BLOG API RESPONSE:", result);

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Something went wrong while saving blog.",
        );
      }

      alert(
        editingId ? "Blog updated successfully!" : "Blog created successfully!",
      );

      resetForm();

      setShowForm(false);

      await fetchBlogs();
    } catch (error) {
      console.error("BLOG SAVE ERROR:", error);

      setError(error.message || "Something went wrong while creating blog.");

      alert(error.message || "Something went wrong while creating blog.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | EDIT BLOG
  |--------------------------------------------------------------------------
  */

  const handleEdit = (blog) => {
    setEditingId(blog.id);

    setFormData({
      title: blog.title || "",

      handle: blog.handle || "",

      author: blog.author || "",

      publishDate: blog.publish_date || "",

      metaTitle: blog.meta_title || "",

      metaDescription: blog.meta_description || "",

      image: null,

      content: blog.content || "",

      status: blog.status || "published",
    });

    setShowForm(true);
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE BLOG
  |--------------------------------------------------------------------------
  */

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this blog?",
    );

    if (!confirmed) return;

    try {
      setDeletingId(id);

      const response = await fetch(
        `${API}?id=${id}`,

        {
          method: "DELETE",
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to delete blog.");
      }

      setBlogs((prev) => prev.filter((blog) => blog.id !== id));

      alert("Blog deleted successfully!");
    } catch (error) {
      console.error("DELETE BLOG ERROR:", error);

      alert(error.message || "Unable to delete blog.");
    } finally {
      setDeletingId(null);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | SEARCH BLOGS
  |--------------------------------------------------------------------------
  */

  const filteredBlogs = blogs.filter((blog) => {
    const query = search.toLowerCase();

    return (
      blog.title?.toLowerCase().includes(query) ||
      blog.handle?.toLowerCase().includes(query) ||
      blog.author?.toLowerCase().includes(query)
    );
  });

  /*
  |--------------------------------------------------------------------------
  | IMAGE URL
  |--------------------------------------------------------------------------
  */

  const getImageUrl = (image) => {
    if (!image) return "";

    if (image.startsWith("http")) {
      return image;
    }

    return `${IMAGE_BASE_URL}${image}`;
  };

  /*
  |--------------------------------------------------------------------------
  | STATS
  |--------------------------------------------------------------------------
  */

  const publishedBlogs = blogs.filter(
    (blog) => blog.status === "published",
  ).length;

  const draftBlogs = blogs.filter((blog) => blog.status === "draft").length;

  return (
    <div className="admin-blogs-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="admin-blogs-header">
        <div>
          <span className="admin-eyebrow">CONTENT MANAGEMENT</span>

          <h1>Blogs</h1>

          <p>Create, manage and publish your blog content.</p>
        </div>

        {hasPermission("blogCreate") && (
          <button className="add-blog-btn" onClick={openCreateForm}>
            <span>+</span>
            Add New Blog
          </button>
        )}
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && <div className="admin-error">{error}</div>}

      {/* =====================================================
          STATS
      ===================================================== */}

      <div className="blog-stats">
        <div className="blog-stat-card">
          <div className="stat-icon">📝</div>

          <div>
            <span>Total Blogs</span>

            <strong>{blogs.length}</strong>
          </div>
        </div>

        <div className="blog-stat-card">
          <div className="stat-icon">✓</div>

          <div>
            <span>Published</span>

            <strong>{publishedBlogs}</strong>
          </div>
        </div>

        <div className="blog-stat-card">
          <div className="stat-icon">◷</div>

          <div>
            <span>Drafts</span>

            <strong>{draftBlogs}</strong>
          </div>
        </div>
      </div>

      {/* =====================================================
          BLOG SECTION
      ===================================================== */}

      <div className="blogs-section">
        <div className="blogs-section-header">
          <div>
            <h2>All Blogs</h2>

            <p>Manage your published blog articles</p>
          </div>

          <div className="blog-search">
            <input
              type="text"
              placeholder="Search blogs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <span>⌕</span>
          </div>
        </div>

        {/* =====================================================
            LOADING
        ===================================================== */}

        {loading ? (
          <div className="empty-blogs">
            <div className="empty-icon">◌</div>

            <h3>Loading blogs...</h3>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="empty-blogs">
            <div className="empty-icon">✦</div>

            <h3>{search ? "No matching blogs found" : "No blogs yet"}</h3>

            <p>
              {search
                ? "Try another search term."
                : "Start creating your first blog post."}
            </p>

            {!search && hasPermission("blogCreate") && (
              <button onClick={openCreateForm} className="empty-add-btn">
                Create Your First Blog
              </button>
            )}
          </div>
        ) : (
          <div className="blogs-grid">
            {filteredBlogs.map((blog) => (
              <div className="admin-blog-card" key={blog.id}>
                {/* IMAGE */}

                <div className="admin-blog-image">
                  {blog.featured_image ? (
                    <img
                      src={getImageUrl(blog.featured_image)}
                      alt={blog.title}
                    />
                  ) : (
                    <div className="no-blog-image">No Image</div>
                  )}

                  <span
                    className={
                      blog.status === "published"
                        ? "published-badge"
                        : "draft-badge"
                    }
                  >
                    {blog.status || "published"}
                  </span>
                </div>

                {/* CONTENT */}

                <div className="admin-blog-content">
                  <h3>{blog.title}</h3>

                  <p>
                    {blog.meta_description || "No meta description available."}
                  </p>

                  <div className="blog-card-footer">
                    <span>{blog.publish_date || "No date"}</span>

                    <div className="blog-card-actions">
                      {hasPermission("blogEdit") && (
                        <button onClick={() => handleEdit(blog)}>Edit</button>
                      )}

                      {hasPermission("blogDelete") && (
                        <button
                          className="delete-action"
                          onClick={() => handleDelete(blog.id)}
                          disabled={deletingId === blog.id}
                        >
                          {deletingId === blog.id ? "Deleting..." : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* =====================================================
          CREATE / EDIT MODAL
      ===================================================== */}

      {showForm && (
        <div className="blog-modal-overlay">
          <div className="blog-modal">
            {/* HEADER */}

            <div className="modal-header">
              <div>
                <span className="admin-eyebrow">BLOG MANAGEMENT</span>

                <h2>{editingId ? "Edit Blog" : "Create New Blog"}</h2>
              </div>

              <button
                className="modal-close"
                onClick={() => {
                  resetForm();

                  setShowForm(false);
                }}
              >
                ×
              </button>
            </div>

            {/* FORM */}

            <form className="blog-form" onSubmit={handleSubmit}>
              <div className="form-grid">
                {/* TITLE */}

                <div className="form-field full-width">
                  <label>Blog Title</label>

                  <input
                    type="text"
                    name="title"
                    placeholder="Enter blog title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* HANDLE */}

                <div className="form-field">
                  <label>URL Handle</label>

                  <input
                    type="text"
                    name="handle"
                    placeholder="example-blog-title"
                    value={formData.handle}
                    onChange={handleChange}
                    required
                  />

                  <small>
                    /blogs/
                    {formData.handle || "your-blog-handle"}
                  </small>
                </div>

                {/* AUTHOR */}

                <div className="form-field">
                  <label>Author</label>

                  <input
                    type="text"
                    name="author"
                    placeholder="Author name"
                    value={formData.author}
                    onChange={handleChange}
                  />
                </div>

                {/* DATE */}

                <div className="form-field">
                  <label>Publish Date</label>

                  <input
                    type="date"
                    name="publishDate"
                    value={formData.publishDate}
                    onChange={handleChange}
                  />
                </div>

                {/* IMAGE */}

                <div className="form-field">
                  <label>Featured Image</label>

                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleChange}
                  />

                  {editingId && (
                    <small>Leave empty to keep current image.</small>
                  )}
                </div>

                {/* META TITLE */}

                <div className="form-field full-width">
                  <label>Meta Title</label>

                  <input
                    type="text"
                    name="metaTitle"
                    placeholder="SEO meta title"
                    value={formData.metaTitle}
                    onChange={handleChange}
                  />
                </div>

                {/* META DESCRIPTION */}

                <div className="form-field full-width">
                  <label>Meta Description</label>

                  <textarea
                    name="metaDescription"
                    rows="3"
                    placeholder="SEO meta description"
                    value={formData.metaDescription}
                    onChange={handleChange}
                  />
                </div>

                {/* CONTENT */}

                <div className="form-field full-width">
                  <label>Blog Content</label>

                  <div className="blog-editor-layout">
                    {/* EDITOR */}

                    <div className="editor-column">
                      <div className="editor-column-header">
                        <h4>Blog Content</h4>

                        <span>Rich Text Editor</span>
                      </div>

                      <div className="editor-mode-buttons">
                        <button
                          type="button"
                          onClick={() => setHtmlMode(false)}
                          className={!htmlMode ? "active" : ""}
                        >
                          Rich Text
                        </button>

                        <button
                          type="button"
                          onClick={() => setHtmlMode(true)}
                          className={htmlMode ? "active" : ""}
                        >
                          HTML
                        </button>
                      </div>

                      {htmlMode ? (
                        <textarea
                          value={formData.content}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              content: e.target.value,
                            }))
                          }
                          placeholder="Paste HTML content here..."
                          className="html-editor"
                        />
                      ) : (
                        <ReactQuill
                          theme="snow"
                          value={formData.content}
                          onChange={handleContentChange}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Start writing your blog content..."
                        />
                      )}
                    </div>

                    {/* PREVIEW */}

                    <div className="preview-column">
                      <div className="preview-column-header">
                        <div>
                          <h4>Live Preview</h4>

                          <span>This is how your content will appear</span>
                        </div>

                        <span className="preview-live">● LIVE</span>
                      </div>

                      <div className="blog-preview">
                        {formData.content ? (
                          <div
                            className="blog-preview-content"
                            dangerouslySetInnerHTML={{
                              __html: formData.content,
                            }}
                          />
                        ) : (
                          <div className="preview-empty">
                            <span>✦</span>

                            <p>Start writing to see your live preview</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACTIONS */}

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    resetForm();

                    setShowForm(false);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="save-blog-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Update Blog"
                      : "Save Blog"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogs;
